import { isAdhocSectionKey } from '@protoss/shared';
import {
  Button,
  CheckIcon,
  DotsVerticalIcon,
  DropdownMenu,
  IconSize,
  InlineEdit,
  ListBulletIcon,
  Pill,
  SkeletonBlock,
  Tooltip,
} from '@protoss/ui';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageErrorState } from '../components/shared/PageErrorState';
import { MainContentShell } from '../components/shell';
import {
  DealViewSurface,
  getDealStatusPresentation,
} from '../features/sales/components/DealViewSurface';
import { AddFunctionalAreaControl } from '../features/sales/components/AddFunctionalAreaControl';
import { DraggableTabBar } from '../features/sales/components/DraggableTabBar';
import { RegenerateMenuButton } from '../features/sales/components/RegenerateMenuButton';
import { SplitPaneLayout } from '../features/sales/components/SplitPaneLayout';
import { useDealWorkspace } from '../features/sales/dealWorkspaceContext';
import { useDealDelete } from '../features/sales/useDealDelete';
import {
  CONTRACT_SECTION_KEY,
  SalesCreateDealSurface,
  STATIC_SOW_WIZARD_CATEGORY_KEYS,
} from './DealWorkspace';

/**
 * DealMain - the center pane of a deal, rendered for BOTH the view
 * (`/so/deal/:dealUuid`) and edit (`/so/deal/:dealUuid/edit`) routes. Using one
 * component for both routes means navigating between them does NOT remount the
 * pane, so a heavy document surface (e.g. the SOW Tiptap editor) stays alive
 * across a view<->edit toggle instead of reloading - only `isEditMode` flips,
 * flowing into the surface as a prop. Mode-specific bits (read-only vs editable
 * questionnaire, header actions, inline title rename) branch on `isEditMode`.
 *
 * All shared deal data comes from the persistent parent (DealWorkspace) via
 * {@link useDealWorkspace}, so neither this swap nor the right rail reloads.
 */
export function DealMain() {
  const navigate = useNavigate();
  const location = useLocation();
  // Edit mode = the `/edit` child route; the index route is read-only. (The
  // create flow renders its own pane, never this Outlet, so the path suffix is
  // the full signal here.)
  const isEditMode = location.pathname.endsWith('/edit');

  const {
    dealUuid,
    appUserUuid,
    salesModuleRole,
    isWorkspaceAdmin,
    deal,
    loadingDeal,
    dealError,
    retryLoadDeal,
    questionnaire,
    loadingQuestionnaire,
    questionnaireError,
    retryLoadQuestionnaire,
    answers,
    persistedAnswers,
    fieldLookup,
    touchedFields,
    setTouchedFields,
    changedFieldKeys,
    savableFieldKeys,
    saveError,
    handleAnswerChange,
    handlePersistAnswers,
    handleReloadQuestionnaire,
    commitAdhocScopeFields,
    isEnrichmentRunning,
    capturingAdhocCount,
    hasPendingAdhocScope,
    dealAttributes,
    handleOpenAttributesPane,
    handleUpdateDealTitle,
    confirmIfDirty,
    dirtyKey,
    documentTabItems,
    activeDocValue,
    switchDocMode,
    documentSurface,
    secondaryDocumentSurface,
    setSecondaryViewingDoc,
    documentHeaderActions,
    documentHeaderStatus,
    focusedAdhocFieldKey,
    setFocusedAdhocFieldKey,
    noScopeCaptured,
  } = useDealWorkspace();

  // Static-SOW categories (managed_services): the questionnaire wizard gets a footer
  // with Previous/Next section stepping. SOW generation is NOT offered from the
  // questionnaire - it lives on the SOW tab, gated on the contract terms.
  const dealCategoryKey = deal?.dealCategoryKey ?? null;
  const sectionStepperFooter =
    dealCategoryKey != null && STATIC_SOW_WIZARD_CATEGORY_KEYS.has(dealCategoryKey);

  const [showAnsweredOnly, setShowAnsweredOnly] = React.useState(true);
  const { openConfirmDelete, confirmDeleteNode } = useDealDelete(dealUuid, deal?.title ?? '');

  // Shared Delete-only kebab, rendered on EVERY deal tab (questionnaire view/edit + every
  // document surface) for non-viewers. Upload document / Enrich company moved to the
  // /upload-document and /enrich-company slash commands.
  const deleteMenu =
    salesModuleRole !== 'viewer' ? (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="deal-page__icon-btn"
            aria-label="Deal options"
            type="button"
          >
            <DotsVerticalIcon aria-hidden="true" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="end">
            <DropdownMenu.Item
              className="dropdown-item dropdown-item--danger"
              onSelect={() => openConfirmDelete()}
            >
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    ) : null;

  // Ad-hoc ("AI-captured") scope sections only make sense for AI-mode categories, where the
  // estimate is built per scope item. Rule-driven categories (e.g. greenfield NetSuite / Rillet)
  // estimate from the fixed questionnaire, so hide the ad-hoc sections for them. Display-only: the
  // raw questionnaire (and any ad-hoc answers) is untouched - this just controls what the
  // questionnaire surfaces and the section picker render.
  const visibleQuestionnaire = React.useMemo(() => {
    if (!questionnaire) return questionnaire;
    // The contract section always renders on its own Contract tab (next to Estimate),
    // never inside the Questionnaire; ad-hoc sections additionally drop for rule-driven
    // (non-AI-mode) categories as before.
    const sections = questionnaire.sections.filter(
      (s) =>
        s.sectionKey !== CONTRACT_SECTION_KEY &&
        (questionnaire.aiMode || !isAdhocSectionKey(s.sectionKey))
    );
    if (sections.length === questionnaire.sections.length) return questionnaire;
    return { ...questionnaire, sections };
  }, [questionnaire]);

  type DocTab =
    | 'scope'
    | 'questionnaire'
    | 'systems-landscape'
    | 'estimate'
    | 'contract'
    | 'resource-plan'
    | 'sow'
    | 'docs';
  const [splitLayout, setSplitLayout] = React.useState<{
    leftTab: DocTab;
    rightTab: DocTab;
  } | null>(null);
  const [draggingTab, setDraggingTab] = React.useState<DocTab | null>(null);
  const [dragOverZone, setDragOverZone] = React.useState<'left' | 'right' | null>(null);

  // Cross-pane focus sync is active only when the split shows Questionnaire + Prioritization
  // together; otherwise focusing a question must not set focus (it would flash with nothing to sync).
  const adhocSyncActive = React.useMemo(() => {
    if (!splitLayout) return false;
    const tabs = new Set<string>([splitLayout.leftTab, splitLayout.rightTab]);
    return tabs.has('questionnaire') && tabs.has('prioritization');
  }, [splitLayout]);

  const allSectionKeys = React.useMemo(
    () => visibleQuestionnaire?.sections.map((s) => s.sectionKey) ?? [],
    [visibleQuestionnaire?.sections]
  );

  const [selectedSectionKeys, setSelectedSectionKeys] = React.useState<string[]>(allSectionKeys);

  React.useEffect(() => {
    setSelectedSectionKeys(allSectionKeys);
  }, [allSectionKeys]);

  // Split-view questionnaire (edit mode): a single save-all button in the pane header
  // replaces the per-group Save. It persists the entire questionnaire via handlePersistAnswers().
  const [savingAll, setSavingAll] = React.useState(false);
  const handleSaveAll = React.useCallback(async () => {
    setSavingAll(true);
    try {
      await handlePersistAnswers();
    } finally {
      setSavingAll(false);
    }
  }, [handlePersistAnswers]);
  const questionnaireHeaderActions = (
    <Button
      variant="primary"
      size="sm"
      disabled={savingAll || (savableFieldKeys.size === 0 && !hasPendingAdhocScope)}
      onClick={() => void handleSaveAll()}
    >
      {savingAll ? <span className="wizard__btn-spinner" aria-hidden="true" /> : 'Save'}
    </Button>
  );

  // `loadingDeal` stays true while the parent polls the deal for enrichment, so
  // gate the skeleton/actions on first-load only (deal not yet present).
  const initialLoading = loadingDeal && !deal;
  const statusPresentation = getDealStatusPresentation(deal?.status, { noScopeCaptured });

  // The center questionnaire surface: editable in edit mode, read-only in view mode.
  const renderQuestionnaire = React.useCallback(
    (compactMode?: boolean) =>
      isEditMode ? (
        <SalesCreateDealSurface
          deal={deal}
          loadingDeal={loadingDeal}
          dealError={dealError}
          onRetryLoadDeal={retryLoadDeal}
          questionnaire={visibleQuestionnaire}
          loadingQuestionnaire={loadingQuestionnaire}
          questionnaireError={questionnaireError}
          onRetryLoadQuestionnaire={retryLoadQuestionnaire}
          answers={answers}
          persistedAnswers={persistedAnswers}
          touchedFields={touchedFields}
          setTouchedFields={setTouchedFields}
          changedFieldKeys={changedFieldKeys}
          savableFieldKeys={savableFieldKeys}
          saveError={saveError}
          onAnswerChange={handleAnswerChange}
          onPersistAnswers={handlePersistAnswers}
          isEnrichmentRunning={isEnrichmentRunning}
          capturingAdhocCount={capturingAdhocCount}
          hasPendingAdhocScope={hasPendingAdhocScope}
          currentUserUuid={appUserUuid}
          salesModuleRole={salesModuleRole}
          dealUuid={dealUuid}
          onReloadQuestionnaire={handleReloadQuestionnaire}
          commitAdhocScopeFields={commitAdhocScopeFields}
          sidebarHeader={
            <div className="deal-page__sidebar-header">
              <span className="deal-page__sidebar-label">Questions</span>
              {deal ? (
                <Pill variant={getDealStatusPresentation(deal.status, { noScopeCaptured }).variant}>
                  {getDealStatusPresentation(deal.status, { noScopeCaptured }).label}
                </Pill>
              ) : null}
              {/* Functional areas are the only capture container (the catch-all "Ad-hoc questions"
                  is gone) - new areas are created here; each area's own + adds questions inside. */}
              {dealUuid && salesModuleRole !== 'viewer' ? (
                <AddFunctionalAreaControl
                  dealUuid={dealUuid}
                  onReloadQuestionnaire={handleReloadQuestionnaire}
                />
              ) : null}
            </div>
          }
          compactMode={compactMode}
          selectedSectionKeys={compactMode ? selectedSectionKeys : undefined}
          onChangeSectionFilter={compactMode ? setSelectedSectionKeys : undefined}
          focusedAdhocFieldKey={focusedAdhocFieldKey}
          onFocusAdhocField={adhocSyncActive ? setFocusedAdhocFieldKey : undefined}
          sectionStepperFooter={sectionStepperFooter}
        />
      ) : (
        <DealViewSurface
          loading={initialLoading || loadingQuestionnaire}
          questionnaire={visibleQuestionnaire}
          answers={answers}
          fieldLookup={fieldLookup}
          showAnsweredOnly={showAnsweredOnly}
          compactMode={compactMode}
          selectedSectionKeys={compactMode ? selectedSectionKeys : undefined}
          focusedAdhocFieldKey={focusedAdhocFieldKey}
          onFocusAdhocField={adhocSyncActive ? setFocusedAdhocFieldKey : undefined}
          canGenerateBprDiagrams={salesModuleRole !== 'viewer'}
        />
      ),
    [
      isEditMode,
      deal,
      loadingDeal,
      dealError,
      retryLoadDeal,
      visibleQuestionnaire,
      loadingQuestionnaire,
      questionnaireError,
      retryLoadQuestionnaire,
      answers,
      persistedAnswers,
      touchedFields,
      setTouchedFields,
      changedFieldKeys,
      savableFieldKeys,
      saveError,
      handleAnswerChange,
      handlePersistAnswers,
      isEnrichmentRunning,
      // capturingAdhocCount + hasPendingAdhocScope are passed straight to the surface; without them
      // here the memoized callback keeps a stale closure and the surface never sees a capture in
      // flight (no ad-hoc skeleton / force-open / nav) or pending ad-hoc edits (Save stays disabled).
      capturingAdhocCount,
      hasPendingAdhocScope,
      noScopeCaptured,
      appUserUuid,
      salesModuleRole,
      dealUuid,
      handleReloadQuestionnaire,
      commitAdhocScopeFields,
      initialLoading,
      fieldLookup,
      showAnsweredOnly,
      selectedSectionKeys,
      focusedAdhocFieldKey,
      adhocSyncActive,
      setFocusedAdhocFieldKey,
      sectionStepperFooter,
    ]
  );

  const handleTabDragStart = (tab: string) => {
    setDraggingTab(tab as DocTab);
  };

  // View-mode questionnaire header: answered/all filter toggle + Edit + Delete kebab.
  const viewHeaderActions =
    dealUuid && !!deal ? (
      <div className="deal-view-page__header-actions">
        <div className="deal-view-page__toggle" role="group" aria-label="Filter fields">
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={`deal-view-page__toggle-btn deal-view-page__toggle-btn--icon${showAnsweredOnly ? ' deal-view-page__toggle-btn--active' : ''}`}
                aria-label="Answered only"
                aria-pressed={showAnsweredOnly}
                onClick={() => setShowAnsweredOnly(true)}
              >
                <CheckIcon width={IconSize.sm} height={IconSize.sm} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
                Answered only
                <Tooltip.Arrow className="ui-tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                className={`deal-view-page__toggle-btn deal-view-page__toggle-btn--icon${!showAnsweredOnly ? ' deal-view-page__toggle-btn--active' : ''}`}
                aria-label="All fields"
                aria-pressed={!showAnsweredOnly}
                onClick={() => setShowAnsweredOnly(false)}
              >
                <ListBulletIcon width={IconSize.sm} height={IconSize.sm} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
                All fields
                <Tooltip.Arrow className="ui-tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </div>
        {salesModuleRole !== 'viewer' && (
          <>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => navigate(`/so/deal/${dealUuid}/edit`)}
            >
              Edit
            </Button>
            {deleteMenu}
          </>
        )}
      </div>
    ) : undefined;

  // Edit-mode questionnaire header: View toggle + Regenerate menu + AI/Delete kebab.
  const editHeaderActions = (
    <div className="sales-company-shell__header-actions">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={async () => {
          const ok = await confirmIfDirty(dirtyKey);
          if (!ok) return;
          setTouchedFields(new Set());
          navigate(`/so/deal/${dealUuid}`);
        }}
      >
        View
      </Button>
      <RegenerateMenuButton />
      {deleteMenu}
    </div>
  );

  return (
    <>
      <MainContentShell
        title={deal?.title ?? deal?.company?.name ?? 'Deal'}
        titleNode={
          <>
            <button
              type="button"
              className="deal-view-page__breadcrumb-link"
              onClick={() => navigate('/so/deals')}
            >
              Deals
            </button>
            <span className="deal-view-page__breadcrumb-sep" aria-hidden="true">
              {' ›'}
            </span>
            {isEditMode ? (
              loadingDeal && !deal ? (
                <SkeletonBlock className="customer-detail__name-skeleton" />
              ) : isWorkspaceAdmin || deal?.createdByUserUuid === appUserUuid ? (
                <InlineEdit
                  value={deal?.title ?? deal?.company?.name ?? 'Deal'}
                  onSave={handleUpdateDealTitle}
                />
              ) : (
                <span>{deal?.title ?? deal?.company?.name ?? 'Deal'}</span>
              )
            ) : (
              <span>{deal?.title ?? deal?.company?.name ?? 'Deal'}</span>
            )}
          </>
        }
        loading={!isEditMode && initialLoading}
        titleMeta={
          <>
            {deal ? (
              dealAttributes.length > 0 ? (
                <>
                  <div className="customer-detail__header-pills">
                    {dealAttributes.map((attr) => {
                      const selectedOption = attr.options.find(
                        (o) => o.valueKey === attr.currentValue
                      );
                      return (
                        <Tooltip.Root key={attr.attributeKey}>
                          <Tooltip.Trigger asChild>
                            <button
                              type="button"
                              className={`deal-attr-pill${attr.currentValue ? '' : ' deal-attr-pill--empty'}`}
                              onClick={handleOpenAttributesPane}
                            >
                              {selectedOption?.label ?? '-'}
                            </button>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content side="bottom" sideOffset={4} className="ui-tooltip">
                              {attr.label}
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="deal-attr-pill deal-attr-pill--collapsed"
                    onClick={handleOpenAttributesPane}
                    aria-label="Open attributes"
                  >
                    Attributes
                  </button>
                </>
              ) : !isEditMode ? (
                <Pill variant={statusPresentation.variant}>{statusPresentation.label}</Pill>
              ) : null
            ) : null}
          </>
        }
        subnav={
          deal && !splitLayout ? (
            <DraggableTabBar
              items={documentTabItems}
              value={activeDocValue}
              onValueChange={switchDocMode}
              onTabDragStart={handleTabDragStart}
              ariaLabel="Deal documents"
              variant="subnav"
            />
          ) : undefined
        }
        actions={
          isEditMode ? (
            <div className="deal-header__edit-actions">
              {documentHeaderStatus}
              <span className="deal-header__edit-badge">Edit mode</span>
              {documentSurface && !splitLayout ? (
                <>
                  {documentHeaderActions}
                  {deleteMenu}
                </>
              ) : (
                editHeaderActions
              )}
            </div>
          ) : documentSurface && !splitLayout ? (
            <>
              {documentHeaderActions}
              {deleteMenu}
            </>
          ) : (
            viewHeaderActions
          )
        }
      >
        {!isEditMode && dealError ? (
          <PageErrorState message={dealError} onRetry={retryLoadDeal} />
        ) : draggingTab ? (
          // Show drop zones when dragging
          <div className="deal-page__drag-zones">
            <div
              className={`deal-page__drop-zone deal-page__drop-zone--left${dragOverZone === 'left' ? ' deal-page__drop-zone--active' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverZone('left');
              }}
              onDragLeave={() => setDragOverZone(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingTab) {
                  setSplitLayout({ leftTab: draggingTab, rightTab: activeDocValue as DocTab });
                  switchDocMode(draggingTab);
                  setSecondaryViewingDoc(
                    activeDocValue === 'questionnaire'
                      ? null
                      : {
                          kind: activeDocValue as
                            | 'scope'
                            | 'systems-landscape'
                            | 'estimate'
                            | 'contract'
                            | 'resource-plan'
                            | 'sow'
                            | 'docs',
                        }
                  );
                }
                setDraggingTab(null);
                setDragOverZone(null);
              }}
            >
              <div className="deal-page__drop-zone-hint">Drop here for left pane</div>
            </div>
            <div
              className={`deal-page__drop-zone deal-page__drop-zone--right${dragOverZone === 'right' ? ' deal-page__drop-zone--active' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverZone('right');
              }}
              onDragLeave={() => setDragOverZone(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingTab) {
                  setSplitLayout({ leftTab: activeDocValue as DocTab, rightTab: draggingTab });
                  setSecondaryViewingDoc(
                    draggingTab === 'questionnaire'
                      ? null
                      : {
                          kind: draggingTab as
                            | 'scope'
                            | 'systems-landscape'
                            | 'estimate'
                            | 'contract'
                            | 'resource-plan'
                            | 'sow'
                            | 'docs',
                        }
                  );
                }
                setDraggingTab(null);
                setDragOverZone(null);
              }}
            >
              <div className="deal-page__drop-zone-hint">Drop here for right pane</div>
            </div>
          </div>
        ) : splitLayout ? (
          <SplitPaneLayout
            questionnaireSurface={renderQuestionnaire(true)}
            leftDocumentSurface={documentSurface}
            rightDocumentSurface={secondaryDocumentSurface}
            leftTab={splitLayout.leftTab}
            rightTab={splitLayout.rightTab}
            allTabs={documentTabItems as unknown as { value: DocTab; label: string }[]}
            onCloseLeft={() => {
              setSplitLayout(null);
              switchDocMode(splitLayout.rightTab);
              setSecondaryViewingDoc(null);
            }}
            onCloseRight={() => {
              setSplitLayout(null);
              setSecondaryViewingDoc(null);
            }}
            onChangeLeftTab={(tab) => {
              setSplitLayout((prev) => (prev ? { ...prev, leftTab: tab } : null));
              switchDocMode(tab);
            }}
            onChangeRightTab={(tab) => {
              setSplitLayout((prev) => (prev ? { ...prev, rightTab: tab } : null));
              setSecondaryViewingDoc(
                tab === 'questionnaire'
                  ? null
                  : {
                      kind: tab as
                        | 'scope'
                        | 'systems-landscape'
                        | 'estimate'
                        | 'contract'
                        | 'resource-plan'
                        | 'sow'
                        | 'docs',
                    }
              );
            }}
            sections={visibleQuestionnaire?.sections ?? []}
            questionnaireHeaderActions={isEditMode ? questionnaireHeaderActions : undefined}
          />
        ) : documentSurface ? (
          <div className="deal-page__doc-surface">{documentSurface}</div>
        ) : (
          renderQuestionnaire()
        )}
      </MainContentShell>

      {confirmDeleteNode}
    </>
  );
}
